import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "@/components/ui/drawer";
import Text from "@/components/Text";
import {
  useState,
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
} from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "react-i18next";

type Props = {
  children: ReactNode;
  filters: string[];
  setFilters: Dispatch<SetStateAction<string[]>>;
};

const DrawerFilter: FC<Props> = ({ children, filters, setFilters }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const onChangeFilter = (state: boolean, filterName: string) => {
    if (state) {
      setFilters([...filters, filterName]);
    } else {
      setFilters(filters.filter((filter) => filter !== filterName));
    }
  };

  return (
    <Drawer
      direction="right"
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
      }}
    >
      <DrawerTrigger>{children}</DrawerTrigger>

      <DrawerContent>
        <DrawerHeader
          style={{
            borderBottom: "1px solid #F1F2F4",
          }}
        >
          <div className="flex justify-between items-center">
            <Text fontSize={24} fontWeight={600}>
              {t("filters")}
            </Text>
            <X
              height={20}
              width={20}
              className="cursor-pointer"
              onClick={() => {
                setOpen(false);
              }}
            />
          </div>
        </DrawerHeader>

        <div className="flex flex-col gap-[32px] p-[24px] overflow-y-auto">
          <div className="flex flex-col gap-[16px]">
            <Text
              fontSize={14}
              fontWeight={400}
              color="#44546F"
              className="uppercase"
            >
              {t("platforms")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("ubereats")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "ubereats")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                Uber Eats
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("deliveroo")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "deliveroo")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                Deliveroo
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-[16px]">
            <Text fontSize={14} fontWeight={400} color="#44546F">
              {t("columns_score_performance")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("krunsh_score_more_less")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "krunsh_score_more_less")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("krunsh_score_more_less", { score: 60 })}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("krunsh_score_more_than")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "krunsh_score_more_than")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("krunsh_score_more_than", { score: 60 })}
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-[16px]">
            <Text fontSize={14} fontWeight={400} color="#44546F">
              {t("columns_reviews")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("average_rating_less_tan")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "average_rating_less_tan")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("average_rating_less_tan", { rating: 4 })}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("average_rating_more_tan")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "average_rating_more_tan")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("average_rating_more_tan", { rating: 4 })}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("rating_count_less_than")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "rating_count_less_than")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("rating_count_less_than", { count: 100 })}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("rating_count_more_than")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "rating_count_more_than")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("rating_count_more_than", { count: 100 })}
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-[16px]">
            <Text fontSize={14} fontWeight={400} color="#44546F">
              {t("columns_delivery")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("average_time_less_than")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "average_time_less_than")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("average_time_less_than", { time: 10 })}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("average_time_more_than")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "average_time_more_than")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("average_time_more_than", { time: 10 })}
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-[16px]">
            <Text fontSize={14} fontWeight={400} color="#44546F">
              {t("columns_promotions")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("option_with_promotion")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "option_with_promotion")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("option_with_promotion")}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("option_without_promotion")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "option_without_promotion")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("option_without_promotion")}
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-[16px]">
            <Text fontSize={14} fontWeight={400} color="#44546F">
              {t("columns_visuals")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("with_hero_image")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "with_hero_image")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("with_hero_image")}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("without_hero_image")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "without_hero_image")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("without_hero_image")}
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-[16px]">
            <Text
              fontSize={14}
              fontWeight={400}
              color="#44546F"
              className="uppercase"
            >
              {t("general")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("open")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "open")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("open")}
              </Text>
            </div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                checked={filters.includes("closed")}
                onCheckedChange={(state) =>
                  onChangeFilter(state as boolean, "closed")
                }
              />
              <Text fontSize={16} fontWeight={400} color="#414651">
                {t("closed")}
              </Text>
            </div>
          </div>
        </div>

        <DrawerFooter
          style={{
            borderTop: "1px solid #F1F2F4",
          }}
        >
          <div className="flex justify-end gap-[8px]">
            <Button
              variant="secondaryFilled"
              onClick={() => {
                setFilters([]);
                setOpen(false);
              }}
            >
              {t("reset")}
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
              }}
            >
              {t("apply")}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default DrawerFilter;
